import { sharePointSourceConnectionId } from '@/features/workspace/knowledge/sharepoint-place-coverage.js';
import type { SharePointConnectionChoice } from '@/features/sharepoint/connection-choice.js';
import type {
  SharePointPlace,
  SharePointPlaceLevel,
} from '@/lib/api/sharepoint.js';
import type {
  KnowledgeSource,
  KnowledgeSourceType,
} from '@/lib/api/workspace-knowledge.js';

/**
 * Where a place stands on the workspace's knowledge shelf. Two states, because every level a person
 * can allow is a level they can index: a site, a library, a folder and a file each register as their own
 * kind of source, so none of them is ever a question with nowhere to ask it.
 */
export type PlaceIndexStanding = 'on' | 'off';

/** The Graph coordinates every level carries, as much of them as that level has. */
export interface PlaceCoordinates {
  siteId: string;
  driveId?: string | null;
  rootItemId?: string | null;
}

/**
 * The knowledge-source address of one allowed place: which kind of source it registers as, and the
 * ids that source is keyed on.
 *
 * A folder or a file is the ordinary case — its own Graph item on its own drive. The two levels above it have
 * no drive item at all, so each anchors on an id it already carries: a library on its drive, a site
 * on itself. That repetition is deliberate, not a placeholder — `itemId` is half the key the indexer
 * addresses every source by, so a level that left it empty would collide with every other source of
 * the same connection.
 */
export interface PlaceKnowledgeAddress {
  type: Extract<
    KnowledgeSourceType,
    | 'sharepoint_site'
    | 'sharepoint_library'
    | 'sharepoint_folder'
    | 'sharepoint_file'
  >;
  siteId: string;
  driveId?: string;
  itemId: string;
}

/**
 * The address a level registers under. Written against the coordinates rather than against a stored
 * place, so the browser can ask it about a row it is about to add and the list can ask it about a row
 * already on the list — one answer, so the two can never disagree about where a place lives.
 */
export function knowledgeAddressOf(
  level: SharePointPlaceLevel,
  coordinates: PlaceCoordinates,
): PlaceKnowledgeAddress | null {
  const { siteId, driveId, rootItemId } = coordinates;
  if (level === 'site') {
    return { type: 'sharepoint_site', siteId, itemId: siteId };
  }
  if (!driveId) return null;
  if (level === 'library') {
    return { type: 'sharepoint_library', siteId, driveId, itemId: driveId };
  }
  if (!rootItemId) return null;
  return {
    type: level === 'file' ? 'sharepoint_file' : 'sharepoint_folder',
    siteId,
    driveId,
    itemId: rootItemId,
  };
}

/** {@link knowledgeAddressOf} for a place already on the list. */
export function placeKnowledgeAddress(
  place: SharePointPlace,
): PlaceKnowledgeAddress | null {
  return knowledgeAddressOf(place.level, place);
}

/**
 * The source registered at a place's own address, or nothing.
 *
 * The match carries the kind as well as the ids. A library and the folder at its root would otherwise
 * read as each other on a drive whose root item id somebody registered by hand, and a site's own id
 * is not a drive item id at all — so the kind is what keeps each level answering only for itself.
 *
 * `connections` are the workspace's SharePoint connections. They are what tells which connection a
 * source with no `connection_id` belongs to.
 */
export function findPlaceKnowledgeSource(
  place: SharePointPlace,
  sources: readonly KnowledgeSource[],
  connectionId: string,
  connections: readonly SharePointConnectionChoice[],
): KnowledgeSource | undefined {
  const address = placeKnowledgeAddress(place);
  if (!address) return undefined;
  return sources.find(
    (source) =>
      source.type === address.type &&
      source.config.site_id === address.siteId &&
      source.config.drive_id === address.driveId &&
      source.config.item_id === address.itemId &&
      /**
       * A source registered before connections had ids carries none, and the indexer reads those
       * against the workspace's shared connection — which is where they came from. So such a source
       * matches that connection only. Matching it for any connection would make it look indexed for
       * the wrong tenant, and removing that tenant's place would delete it.
       */
      sharePointSourceConnectionId(source, connections) === connectionId,
  );
}

/** Whether this exact place is registered, in the connection it belongs to. */
export function placeIndexStanding(
  place: SharePointPlace,
  sources: readonly KnowledgeSource[],
  connectionId: string,
  connections: readonly SharePointConnectionChoice[],
): PlaceIndexStanding {
  return findPlaceKnowledgeSource(place, sources, connectionId, connections)
    ? 'on'
    : 'off';
}
